import { useEffect, useState, type ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusPill, APPLICATION_STATUS_TONES, APPLICATION_STATUS_LABELS } from "@/components/StatusPill";

const LADDER = ["AwaitingAgentPricing", "Submitted", "ESignSent", "ESigned", "ActionNeeded", "Approved", "Boarded", "Live"] as const;

const LADDER_POSITION: Record<string, string> = {
  New: "Submitted",
  Pending: "Submitted",
  Underwriting: "ESigned",
};

const WAITING_ON: Record<string, string> = {
  AwaitingAgentPricing: "Agent",
  Submitted: "Onboarding team",
  New: "Onboarding team",
  Pending: "Onboarding team",
  ESignSent: "Merchant",
  ESigned: "Underwriting",
  Underwriting: "Underwriting",
  ActionNeeded: "Agent",
  Approved: "Coding",
  Boarded: "First deposit",
};

const AGENT_TURN = new Set(["AwaitingAgentPricing", "ActionNeeded"]);

export const waitingOn = (status: string, agentName?: string | null): string | null =>
  AGENT_TURN.has(status) && agentName ? agentName : WAITING_ON[status] ?? null;

export const NEXT_STEP: Record<string, string> = {
  AwaitingAgentPricing: "Configure pricing",
  ActionNeeded: "Reply to underwriting",
};

const AGENT_TIPS: Record<string, { tone: string; text: string }> = {
  Submitted: { tone: "border-blue-200 bg-blue-50 text-blue-900", text: "With the onboarding team. You can still change pricing until they submit it for processing." },
  ESignSent: { tone: "border-blue-200 bg-blue-50 text-blue-900", text: "Waiting on the merchant to e-sign. Ask them to check their inbox (and spam) for an email from Merchant Services (notifications@assuresign.com)." },
  ESigned: { tone: "border-blue-200 bg-blue-50 text-blue-900", text: "Signed and with underwriting. Nothing for you to do right now." },
  Approved: { tone: "border-emerald-200 bg-emerald-50 text-emerald-900", text: "🎉 Approved! The merchant is being set up." },
  Boarded: { tone: "border-emerald-200 bg-emerald-50 text-emerald-900", text: "The merchant account is set up. Waiting on their first deposit." },
  Live: { tone: "border-emerald-200 bg-emerald-50 text-emerald-900", text: "💸 First deposit made. This merchant is Live." },
};

export function AgentTip({ status }: { status: string }) {
  const tip = AGENT_TIPS[status];
  if (!tip) return null;
  return <div className={cn("mb-4 rounded-lg border px-4 py-3 text-sm", tip.tone)}>{tip.text}</div>;
}

export function timeInStatus(since: string | null, now: number = Date.now()): string | null {
  if (!since) return null;
  const start = new Date(since).getTime();
  if (Number.isNaN(start)) return null;
  const minutes = Math.max(0, Math.floor((now - start) / 60_000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return `${days} ${days === 1 ? "day" : "days"}${rest ? ` ${rest} h` : ""}`;
}

export function useMinuteTick(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

export function PendedPill({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400" title="Open the underwriting chat" aria-label="Pended – open underwriting chat">
      <StatusPill value="ActionNeeded" map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
    </button>
  );
}

export function StatusTracker({ status, statusSince, agentName, onOpenPended, aside }: { status: string; statusSince: string | null; agentName?: string | null; onOpenPended?: () => void; aside?: ReactNode }) {
  const at = LADDER_POSITION[status] ?? status;
  const current = (LADDER as readonly string[]).indexOf(at);
  const waiting = waitingOn(status, agentName);
  const now = useMinuteTick();
  const day = timeInStatus(statusSince, now);

  if (current < 0) {
    return (
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-slate-600">
        <StatusPill value={status} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
        {day && <span>⏱ {day}</span>}
        {aside && <div className="ml-auto flex flex-col items-end gap-1.5 text-xs">{aside}</div>}
      </div>
    );
  }

  const steps = LADDER.filter((s) => s !== "ActionNeeded" || at === "ActionNeeded");
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-x-6 gap-y-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="min-w-0">
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-2" aria-label="Application status">
        {steps.map((s, i) => {
          const idx = (LADDER as readonly string[]).indexOf(s);
          const done = idx < current;
          const now = s === at;
          return (
            <li key={s} className="flex items-center gap-1">
              {i > 0 && <span className="h-px w-3 bg-slate-300" aria-hidden="true" />}
              {now && status === "ActionNeeded" && onOpenPended ? (
                <PendedPill onClick={onOpenPended} />
              ) : now ? (
                <StatusPill value={status} map={APPLICATION_STATUS_TONES} labels={APPLICATION_STATUS_LABELS} />
              ) : (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
                    done ? "text-emerald-700" : "text-slate-400",
                  )}
                >
                  {done && <Check className="h-3 w-3" aria-hidden="true" />}
                  {APPLICATION_STATUS_LABELS[s] ?? s}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {(waiting || day) && (
        <p className="mt-2 text-xs text-slate-500">
          {waiting && <>Waiting on <span className="font-medium text-slate-700">{waiting}</span></>}
          {waiting && day && " · "}
          {day && <>⏱ {day} in this status</>}
        </p>
      )}
      </div>
      {aside && <div className="flex flex-col items-start gap-1.5 text-xs sm:items-end">{aside}</div>}
    </div>
  );
}
